/**
 * marketingChrome — PurchasePuffin's serializable marketing-website chrome
 * descriptor (BLOG-STD-001, full-shell SSR).
 *
 * `buildMarketingChromeDescriptor({ t })` returns a pure, JSON-able description
 * of the header + footer chrome (nav links, footer columns, social links,
 * locale switcher). It is the SINGLE SOURCE consumed by:
 *   - `createMarketingApp` (client), handed this BUILDER by `MarketingWebsiteApp`, which runs
 *     `buildShellProps` on every render in the language in force (Q-LC24-7).
 *   - the build-time descriptor-gen Vite plugin → POSTed to marketing-cms → the
 *     per-app on-demand prerender harness renders the identical shell server-side.
 *
 * Because BOTH the client and the server resolve `buildShellProps(descriptor,
 * locale)` from the SAME descriptor, the prerendered HTML hydrates with zero
 * chrome mismatch. The descriptor carries NO React nodes + NO class references
 * (e.g. StorageService) — those are injected by `buildShellProps` at call time.
 *
 * @module portals/marketing-website/marketingChrome
 */

import { STORAGE_TYPE } from '@puffin-core/storage';
import { LOCALE_STORAGE_KEY } from './i18n/constants';
import {
  PURCHASE_MARKETING_ANCHORS,
  PURCHASE_MARKETING_ROUTES,
  homeAnchorPath,
} from './constants/purchaseMarketingRoutePaths';

function buildHeaderNavLinks(t) {
  return [
    { to: PURCHASE_MARKETING_ROUTES.FEATURES, label: t('nav.features') },
    { to: PURCHASE_MARKETING_ROUTES.PRICING, label: t('nav.pricing') },
    { to: PURCHASE_MARKETING_ROUTES.BLOG, label: t('nav.blog', { defaultValue: 'Blog' }) },
    { to: PURCHASE_MARKETING_ROUTES.CONTACT, label: t('nav.contact') },
  ];
}

function buildFooterColumns(t) {
  return [
    {
      key: 'product',
      heading: t('footer.product'),
      links: [
        { key: 'features', label: t('footer.features'), to: PURCHASE_MARKETING_ROUTES.FEATURES },
        { key: 'pricing', label: t('footer.pricing'), to: PURCHASE_MARKETING_ROUTES.PRICING },
        {
          key: 'howItWorks',
          label: t('footer.howItWorks'),
          // The landing page's How It Works SECTION, not a route of its own —
          // hence the anchor constant the section itself pins.
          to: homeAnchorPath(PURCHASE_MARKETING_ANCHORS.HOW_IT_WORKS),
        },
      ],
    },
    {
      key: 'company',
      heading: t('footer.company'),
      links: [
        { key: 'contact', label: t('footer.contactUs'), to: PURCHASE_MARKETING_ROUTES.CONTACT },
      ],
    },
    {
      key: 'legal',
      heading: t('footer.legal'),
      links: [
        { key: 'privacy', label: t('footer.privacy'), to: PURCHASE_MARKETING_ROUTES.PRIVACY },
        { key: 'terms', label: t('footer.terms'), to: PURCHASE_MARKETING_ROUTES.TERMS },
      ],
    },
  ];
}

function buildSocialLinks(t) {
  return [
    {
      key: 'linkedin',
      label: t('footer.social.followOn', { platform: 'LinkedIn' }),
      href: 'https://linkedin.com/company/purchasepuffin',
    },
    {
      key: 'x',
      label: t('footer.social.followOn', { platform: 'X' }),
      href: 'https://x.com/purchasepuffin',
    },
  ];
}

/**
 * Build the serializable chrome descriptor for the active locale's `t`.
 *
 * @param {{ t: (key: string, opts?: object) => string }} args
 * @returns {import('@puffin-core/marketing-shell').ChromeDescriptor}
 */
export default function buildMarketingChromeDescriptor({ t }) {
  return {
    appKey: 'purchase',
    header: {
      navLinks: buildHeaderNavLinks(t),
      localeSwitcher: {
        languages: [
          { code: 'en', label: 'English' },
          { code: 'es', label: 'Español' },
        ],
        label: 'Language',
        storageKey: LOCALE_STORAGE_KEY,
        storageTypeLocal: STORAGE_TYPE.LOCAL,
      },
      signInCta: { label: t('nav.contact'), href: PURCHASE_MARKETING_ROUTES.CONTACT },
      // MKT-LOGIN-XDOMAIN-001 (2026-06-29): the "Sign In" CTA routes to the
      // in-portal cross-domain login page (/login), where a tenant admin enters
      // their company subdomain + credentials and is redirected to their own
      // tenant ({tenant}.purchasepuffin.com). Replaces the prior env-driven
      // sign-in indirection that fell back to the contact page — login is now
      // first-class.
      secondaryCta: {
        label: t('nav.signIn', { defaultValue: 'Sign In' }),
        href: PURCHASE_MARKETING_ROUTES.LOGIN,
      },
    },
    footer: {
      tagline: t('footer.description'),
      columns: buildFooterColumns(t),
      socialLinks: buildSocialLinks(t),
      privacyLabel: t('footer.privacy'),
      termsLabel: t('footer.terms'),
    },
  };
}
